import { useEffect, useRef, useState } from 'react';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { isObject } from '@iconik-io/core/src/is_object';
import { EndpointError } from '@iconik-io/iconik-api/src/client/response';

import {
  cuttingRoomProjectLoaded,
  setCuttingRoomProjectTitle,
} from '../../ducks/cutting_room/slice';
import { fetchCuttingRoomEmbedToken } from '../../ducks/cutting_room/thunks';
import { errorMessage } from '../../ducks/messages/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import { cuttingRoomBridge } from './cutting_room_bridge';
import { buildCuttingRoomPlayerUrl } from './cutting_room_config';

const logger = DebugLogger.getInstance('cutting_room');

/**
 * Embed tokens are single use, so one is minted per attempt rather than cached.
 * The budget covers the initial sign-in plus CuttingRoom rejecting a token it
 * should have accepted, and resets once a session has held for
 * `SESSION_STABLE_MS` so a long-running panel can keep re-minting as its
 * session lapses.
 */
const MAX_AUTHORIZE_ATTEMPTS = 3;

/**
 * How long an `authorized` has to hold before the sign-in counts as done.
 * CuttingRoom can accept a token and then reboot signed out (seen in Safari),
 * which reads as `authorized` followed by a fresh `cuttingroom_loaded` and
 * `unauthorized`. Resetting the budget on the `authorized` alone would retry
 * that forever.
 */
const SESSION_STABLE_MS = 5_000;

/**
 * Deadline for the whole sign-in, until the session is stable. Without it a
 * CuttingRoom that never boots, or that answers a posted token with neither
 * `authorized` nor `unauthorized`, would leave the loading bar up forever.
 */
const SIGN_IN_TIMEOUT_MS = 30_000;

type AuthorizationStatus = 'pending' | 'authorized' | 'failed';

/**
 * The mint's HTTP failures are `httpErrorHandlerMiddleware`'s to toast, so only
 * a rejection from outside the request needs a message here.
 */
function getUnreportedErrorMessage(error: unknown): string | undefined {
  if (error instanceof EndpointError) {
    return undefined;
  }

  return error instanceof Error
    ? error.message
    : 'Failed to sign in to CuttingRoom';
}

type UseCuttingRoomIframeOptions = {
  /** A normalized origin, as returned by `selectCuttingRoomBaseUrl`. */
  baseUrl: string;
  view: 'player' | 'project';
  projectId?: string;
  hide?: string[];
};

export function useCuttingRoomIframe({
  baseUrl,
  view,
  projectId,
  hide,
}: UseCuttingRoomIframeOptions) {
  const dispatch = useAppDispatch();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [authorizationStatus, setAuthorizationStatus] =
    useState<AuthorizationStatus>('pending');

  const src = buildCuttingRoomPlayerUrl({
    baseUrl,
    view,
    projectId,
    hide,
  });

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    // The message handler branches on all of these, so they are kept as plain
    // locals rather than state: a re-render would hand it a stale closure.
    let cancelled = false;
    let status: AuthorizationStatus = 'pending';
    let hasBooted = false;
    let attempts = 0;
    let signInTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let sessionStableTimeoutId: ReturnType<typeof setTimeout> | null = null;

    setAuthorizationStatus('pending');

    const clearSignInTimeout = () => {
      if (signInTimeoutId) {
        clearTimeout(signInTimeoutId);
        signInTimeoutId = null;
      }
    };

    const clearSessionStableTimeout = () => {
      if (sessionStableTimeoutId) {
        clearTimeout(sessionStableTimeoutId);
        sessionStableTimeoutId = null;
      }
    };

    const armSessionStableTimeout = () => {
      clearSessionStableTimeout();
      sessionStableTimeoutId = setTimeout(() => {
        sessionStableTimeoutId = null;
        clearSignInTimeout();
        attempts = 0;
      }, SESSION_STABLE_MS);
    };

    const fail = (message?: string) => {
      clearSignInTimeout();
      clearSessionStableTimeout();
      status = 'failed';
      setAuthorizationStatus('failed');
      // Revealing the iframe falls back to CuttingRoom's own login form, which
      // is a dead end on its own, so say why here, unless something else
      // already did.
      if (message) {
        dispatch(errorMessage({ text: message }));
      }
    };

    /**
     * Mint a fresh embed token and post it to the iframe.
     *
     * CuttingRoom only installs its message handler once it has booted, so this
     * is a no-op until `cuttingroom_loaded` arrives; that branch replays it.
     */
    const authorize = async () => {
      if (!hasBooted || status === 'failed') {
        return;
      }

      if (attempts >= MAX_AUTHORIZE_ATTEMPTS) {
        logger.debug('Giving up on CuttingRoom embed authorization');
        fail('Could not sign in to CuttingRoom');
        return;
      }
      attempts += 1;

      try {
        const embedToken = await dispatch(fetchCuttingRoomEmbedToken());
        if (cancelled) {
          return;
        }
        cuttingRoomBridge.authorize(embedToken);
      } catch (error) {
        if (cancelled) {
          return;
        }
        logger.debug('Failed to authorize CuttingRoom embed:', error);
        fail(getUnreportedErrorMessage(error));
      }
    };

    cuttingRoomBridge.registerIframe(iframe, { origin: baseUrl });

    signInTimeoutId = setTimeout(() => {
      signInTimeoutId = null;
      logger.debug('CuttingRoom sign-in did not complete in time');
      fail('CuttingRoom did not complete sign-in');
    }, SIGN_IN_TIMEOUT_MS);

    const unsubscribe = cuttingRoomBridge.subscribe((event) => {
      if (event.event === 'authorized') {
        status = 'authorized';
        setAuthorizationStatus('authorized');
        armSessionStableTimeout();
      }

      if (event.event === 'unauthorized') {
        clearSessionStableTimeout();
        if (status !== 'failed') {
          status = 'pending';
          setAuthorizationStatus('pending');
        }
        // Either the iframe has no session yet, or it rejected our token. Both
        // are answered the same way: send a freshly minted one.
        authorize();
      }

      if (event.event === 'cuttingroom_loaded') {
        // A boot restarts the session-stable wait rather than ending it: an
        // iframe that still has a session from an earlier embed reports
        // `authorized` before it boots, and nothing else would follow to
        // complete the sign-in.
        if (status === 'authorized') {
          armSessionStableTimeout();
        }
        hasBooted = true;
        // An `unauthorized` that arrived before the iframe booted left its
        // authorize deferred; this is the earliest it can be delivered.
        if (status === 'pending') {
          authorize();
        }
      }

      if (
        event.event === 'project_event' &&
        (event.payload?.key === 'PROJECT_LOADED' ||
          event.payload?.key === 'PROJECT_CREATED')
      ) {
        const data = event.payload.data;
        if (isObject(data) && typeof data.title === 'string') {
          dispatch(setCuttingRoomProjectTitle(data.title));
        }
      }

      // `PROJECT_CREATED` only says the project exists, not that it is open, so
      // only a load can end a pending create.
      if (
        event.event === 'project_event' &&
        event.payload?.key === 'PROJECT_LOADED'
      ) {
        dispatch(cuttingRoomProjectLoaded());
      }
    });

    return () => {
      cancelled = true;
      clearSignInTimeout();
      clearSessionStableTimeout();
      unsubscribe();
      cuttingRoomBridge.unregisterIframe();
    };
    // Only `baseUrl` re-registers. `view`, `projectId` and `hide` all land in
    // the URL fragment, so changing them is a same-document navigation the
    // CuttingRoom SPA handles itself — the iframe keeps its document and its
    // session, and re-authorizing would be pointless.
  }, [baseUrl]);

  const iframeProps = {
    ref: iframeRef,
    src,
    title: 'CuttingRoom Player',
    allow:
      'autoplay; fullscreen; microphone; camera; midi; encrypted-media; picture-in-picture; display-capture; clipboard-read; clipboard-write',
  } as const;

  return { iframeProps, authorizationStatus };
}
